import api from '@/utils/ky';
import { Popover, PopoverButton } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { NavLink } from 'react-router-dom';

// Updated interface to match actual API response
interface ApiResponse {
  user: {
    id: string;
    name: string;
    function: string;
    slackId: string;
    workspace: {
      teamId: string;
      teamName: string;
    };
  };
}

export function Sidebar({ token }: { token?: string }) {
  // Fetch real user data from API
  const {
    data: apiResponse,
    isLoading,
    error,
  } = useQuery<ApiResponse>({
    queryKey: ['user', token],
    queryFn: async () => {
      if (!token) return null;

      return api
        .post('api/user/me', {
          json: { token },
        })
        .json();
    },
    enabled: !!token, // Only run query if token exists
  });

  // Get the user name from the response, or use fallback
  const userName = apiResponse?.user?.name || 'Guest User';

  return (
    <aside className="fixed inset-y-0 z-50 flex min-h-screen w-64 flex-col justify-between border-r border-[#E0E0E0] bg-[#F6F8F8] p-2">
      <div>
        <img
          className="w-20 mt-3 ml-3"
          src="/logos/Hupo_Logotype_Orange(noR).svg"
          alt="Hupo logo"
        />
        <div className="mt-4 space-y-1">
          <NavLink
            to={`/en/sessions/past?token=${token}`}
            className={({ isActive }) =>
              clsx(
                'block w-full rounded-lg p-3 text-left tracking-tight hover:bg-[#E3E5E7]',
                isActive && 'bg-[#E3E5E7]',
              )
            }
          >
            Past sessions
          </NavLink>
        </div>
      </div>
      <Popover>
        <PopoverButton className="flex w-full items-center gap-3 gap-x-4 rounded-xl p-3 text-sm/6 font-semibold text-[#272B32] hover:bg-gray-200 focus:outline-none data-[focus]:ring-1 data-[focus]:ring-primary">
          {isLoading ? (
            <div className="size-9 rounded-full bg-gray-200 animate-pulse"></div>
          ) : (
            <img
              className="size-9 rounded-full bg-gray-50"
              alt={userName}
              src="https://cdn.auth0.com/avatars/default.png"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="flex flex-col items-start">
            <span className="sr-only">Your profile</span>
            {isLoading ? (
              <>
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-3 w-16 bg-gray-200 rounded mt-1 animate-pulse"></div>
              </>
            ) : (
              <>
                <span aria-hidden="true">{userName}</span>
                <span
                  aria-hidden="true"
                  className="font-normal leading-5 text-gray"
                >
                  {apiResponse?.user?.workspace?.teamName || 'No Company'}
                </span>
              </>
            )}
          </div>
        </PopoverButton>
        {/* TODO: implement logout late */}
        {/* <PopoverPanel
          transition
          anchor={{ to: 'bottom start', gap: '8px' }}
          className="z-50 w-[var(--button-width)] rounded-lg border border-[#E1E0DF] bg-white p-2 transition duration-200 ease-in-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
        >
          <Button
            className="w-full rounded-lg p-2 text-left text-base/6 tracking-tight"
            onClick={() => console.log('Logout clicked')}
          >
            <img
              className="mr-2 inline-block h-5 w-5"
              src={logout}
              alt="Logout icon"
            />
            Log out
          </Button>
        </PopoverPanel> */}
      </Popover>
    </aside>
  );
}
